import { Component, computed, inject, input, linkedSignal, output } from '@angular/core';
import {
  Button,
  InputLabel,
  InputSimple,
  NotificationService,
  SimpleDialogLayout,
} from 'excellence-ui';
import { LocationsStore } from '../../store/locations.store';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
  getEmptyLocationCreateDialogModel,
  LocationCreateDialogFormModel,
  toLocationPayload,
} from './definitions';
import { FormField, form, required } from '@angular/forms/signals';
import { FieldErrors } from '../../../../shared/components/field-errors/field-errors';

@Component({
  selector: 'app-location-create-dialog',
  templateUrl: './location-create-dialog.html',
  imports: [
    Button,
    InputSimple,
    InputLabel,
    SimpleDialogLayout,
    TranslocoPipe,
    FormField,
    FieldErrors,
  ],
})
export class LocationCreateDialog {
  private notificationService = inject(NotificationService);
  private translocoService = inject(TranslocoService);
  protected locationsStore = inject(LocationsStore);

  clickCancel = output();
  createSuccess = output<string>();

  locationID = input<string | null>(null);

  protected location = computed(
    () => this.locationsStore.locations()?.find((loc) => loc.id === this.locationID()) ?? null,
  );
  protected locationData = computed(() => this.location()?.data ?? null);

  protected isEditLoading = computed(() => this.location()?.isEditLoading ?? false);

  locationModel = linkedSignal<LocationCreateDialogFormModel>(() => {
    const data = this.locationData();
    const emptyData = getEmptyLocationCreateDialogModel();
    if (data === null) {
      return emptyData;
    }
    return {
      ...data,
      name: data.name || emptyData.name,
      address: {
        street: data.address.street || emptyData.address.street,
        streetNumber: data.address.streetNumber || emptyData.address.streetNumber,
        streetNumberSuffix: data.address.streetNumberSuffix || emptyData.address.streetNumberSuffix,
        city: data.address.city || emptyData.address.city,
        zipCode: data.address.zipCode || emptyData.address.zipCode,
      },
    };
  });

  protected locationForm = form(this.locationModel, (schemaPath) => {
    required(schemaPath.address.street);
    required(schemaPath.address.streetNumber);
    required(schemaPath.address.city);
    required(schemaPath.address.zipCode);
  });

  protected async onSubmit(): Promise<void> {
    if (this.locationForm().invalid()) {
      const errorMsg = this.translocoService.translate(
        'cockpit.locations.createDialog.formInvalid',
      );
      this.notificationService.showError({
        title: errorMsg,
      });
      this.markAsTouched();
      return;
    }
    const id = await this.saveData(this.locationForm().value());
    if (id && this.locationsStore.createError() === null) {
      this.locationModel.set(getEmptyLocationCreateDialogModel());
      this.locationForm().reset();
      this.createSuccess.emit(id);
    }
  }

  private markAsTouched() {
    this.locationForm.name().markAsTouched();
    this.locationForm.address.zipCode().markAsTouched();
    this.locationForm.address.city().markAsTouched();
    this.locationForm.address.street().markAsTouched();
    this.locationForm.address.streetNumber().markAsTouched();
    this.locationForm.address.streetNumberSuffix().markAsTouched();
  }

  private async saveData(model: LocationCreateDialogFormModel) {
    const id = this.locationID();
    const payload = toLocationPayload(model);
    if (id) {
      await this.locationsStore.update(id, payload);
      return id;
    } else {
      return await this.locationsStore.create(payload);
    }
  }
}
